import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Group, LoadingOverlay, Paper, Select, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../config/baseUrls";

// Failed payments as Razorpay reported them (GET /admin/payment-failures, newest first). Read-only.
// Use it to see why buyers fail (timeouts, cancels, a method we do not accept) and to follow up with
// buyers who did not pay later.

const PAGE_SIZE = 50;

const getAdminHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
});

const formatTime = (iso) =>
  new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const rupees = (n) => (n == null ? "-" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);

// What each Razorpay reason means for us, in plain words
const REASON_HELP = {
  payment_timed_out: "Buyer did not approve in time",
  payment_cancelled: "Buyer cancelled, or the UPI app was slow",
  instrument_not_accepted: "Payment method not accepted by our Razorpay account",
  bank_account_invalid: "Bank could not process this account type",
};
const reasonColor = (reason) => (reason === "instrument_not_accepted" ? "red" : reason === "payment_timed_out" ? "orange" : "gray");

const ORDER_LABEL = { TICKET_ORDER: "Ticket", PRODUCT_ORDER: "Shop", ADDON_ORDER: "Add-on", SHOPFRONT_ORDER: "Shopfront" };

const EMPTY = { reason: "", method: "", from: "", to: "" };

export default function PaymentFailuresPage() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const { showError } = useNotification();

  const load = useCallback(
    async (before) => {
      setLoading(true);
      try {
        const query = new URLSearchParams({ limit: PAGE_SIZE });
        if (applied.reason) query.set("reason", applied.reason);
        if (applied.method) query.set("method", applied.method);
        if (applied.from) query.set("from", new Date(`${applied.from}T00:00:00`).toISOString());
        if (applied.to) query.set("to", new Date(`${applied.to}T23:59:59`).toISOString());
        if (before) query.set("before", before);
        const res = await fetch(`${API_BASE_URL}/admin/payment-failures?${query}`, { headers: getAdminHeaders() });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          showError("Error", data.error || data.message || "Could not load failed payments.");
          return;
        }
        setRows((prev) => (before ? [...prev, ...data.data] : data.data));
        setHasMore(data.data.length === PAGE_SIZE);
        if (!before) setSummary(data.summary);
      } catch {
        showError("Error", "Network error while loading failed payments.");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [applied],
  );

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.currentTarget.value }));
  const search = (e) => {
    e?.preventDefault();
    setApplied(filters);
  };
  const clear = () => {
    setFilters(EMPTY);
    setApplied(EMPTY);
  };
  const pickReason = (reason) => {
    const next = { ...filters, reason: filters.reason === reason ? "" : reason };
    setFilters(next);
    setApplied(next);
  };

  return (
    <Stack gap="md" data-testid="payment-failures-page">
      <div>
        <Title order={3}>Failed Payments</Title>
        <Text c="dimmed" size="sm">
          Every failed payment with the reason Razorpay gave, newest first. "Paid later" means the same buyer paid for the
          same event afterwards, so no follow-up is needed.
        </Text>
      </div>

      {summary && (
        <Paper withBorder p="md" radius="md" data-testid="payment-failures-summary">
          <Group gap="lg" align="flex-start" wrap="wrap">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase">
                Failures in this search
              </Text>
              <Text fw={700} fz={28}>
                {summary.total}
                {summary.truncated ? "+" : ""}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" mb={4}>
                By reason (click to filter)
              </Text>
              <Group gap="xs">
                {summary.byReason.map((r) => (
                  <Badge
                    key={r.value}
                    size="lg"
                    variant={applied.reason === r.value ? "filled" : "light"}
                    color={reasonColor(r.value)}
                    style={{ cursor: "pointer" }}
                    onClick={() => pickReason(r.value)}
                    title={REASON_HELP[r.value] || ""}
                  >
                    {r.value} {r.count}
                  </Badge>
                ))}
                {summary.byReason.length === 0 && <Text size="sm" c="dimmed">None</Text>}
              </Group>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" mb={4}>
                By method
              </Text>
              <Group gap="xs">
                {summary.byMethod.map((m) => (
                  <Badge key={m.value} size="lg" variant="outline" color="gray">
                    {m.value} {m.count}
                  </Badge>
                ))}
              </Group>
            </div>
          </Group>
        </Paper>
      )}

      <Paper withBorder p="md" radius="md" component="form" onSubmit={search}>
        <Group align="flex-end" wrap="wrap">
          <Select
            label="Method"
            placeholder="All"
            data={[
              { value: "upi", label: "UPI" },
              { value: "card", label: "Card" },
              { value: "netbanking", label: "Netbanking" },
              { value: "wallet", label: "Wallet" },
            ]}
            value={filters.method || null}
            onChange={(v) => setFilters((f) => ({ ...f, method: v || "" }))}
            clearable
            w={160}
          />
          <TextInput label="Reason" placeholder="e.g. payment_timed_out" value={filters.reason} onChange={setFilter("reason")} w={240} />
          <TextInput label="From" type="date" value={filters.from} onChange={setFilter("from")} w={160} />
          <TextInput label="To" type="date" value={filters.to} onChange={setFilter("to")} w={160} />
          <Button type="submit">Search</Button>
          <Button variant="subtle" onClick={clear}>
            Clear
          </Button>
        </Group>
      </Paper>

      <Paper withBorder radius="md" pos="relative" style={{ overflowX: "auto" }}>
        <LoadingOverlay visible={loading} />
        <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
          <Table.Thead bg="gray.0">
            <Table.Tr>
              <Table.Th>Time</Table.Th>
              <Table.Th>Buyer</Table.Th>
              <Table.Th>Event / order</Table.Th>
              <Table.Th>Amount</Table.Th>
              <Table.Th>Reason</Table.Th>
              <Table.Th>Method</Table.Th>
              <Table.Th>Paid later?</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.length === 0 && !loading && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text c="dimmed" ta="center" py="lg">
                    No failed payments for this search.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {rows.map((row) => (
              <Table.Tr key={row.id} data-testid="payment-failure-row">
                <Table.Td>
                  <Text size="sm">{formatTime(row.createdAt)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600}>
                    {row.user?.nickname || row.user?.email || "-"}
                  </Text>
                  {row.user?.email && (
                    <Text size="xs" c="dimmed">
                      {row.user.email}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm" lineClamp={1} maw={260}>
                    {row.productTitle || ORDER_LABEL[row.orderType] || row.orderType}
                  </Text>
                  <Text size="xs" c="dimmed" ff="monospace">
                    {ORDER_LABEL[row.orderType] || row.orderType} {row.order?.id || ""}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{rupees(row.amount)}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" color={reasonColor(row.reason)} title={row.description || ""}>
                    {row.reason}
                  </Badge>
                  {REASON_HELP[row.reason] && (
                    <Text size="xs" c="dimmed" maw={240}>
                      {REASON_HELP[row.reason]}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{row.method || "-"}</Text>
                </Table.Td>
                <Table.Td>
                  {row.orderType === "TICKET_ORDER" ? (
                    <Badge color={row.paidLater ? "green" : "red"} variant="light">
                      {row.paidLater ? "Yes" : "No"}
                    </Badge>
                  ) : (
                    <Text size="sm" c="dimmed">
                      -
                    </Text>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {hasMore && (
        <Group justify="center">
          <Button variant="default" loading={loading} onClick={() => load(rows[rows.length - 1]?.createdAt)}>
            Load more
          </Button>
        </Group>
      )}
    </Stack>
  );
}
